import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Loader2, FileText, MapPin, Clock, User, Activity, Filter, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ActivityLogsPage = () => {
  const { t } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('all');
  const [filterUser, setFilterUser] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, [filterAction, filterUser, startDate, endDate]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      
      if (filterAction && filterAction !== 'all') {
        params.append('action', filterAction);
      }
      if (filterUser) {
        params.append('admin_username', filterUser);
      }
      if (startDate) {
        params.append('start_date', new Date(startDate).toISOString());
      }
      if (endDate) {
        // Set end date to end of day
        const endDateTime = new Date(endDate);
        endDateTime.setHours(23, 59, 59, 999);
        params.append('end_date', endDateTime.toISOString());
      }
      params.append('limit', '50');

      const response = await axios.get(
        `${BACKEND_URL}/api/admin/logs?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      success: 'bg-green-100 text-green-800 border-green-200',
      failure: 'bg-red-100 text-red-800 border-red-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };

    return (
      <Badge className={`${variants[status] || variants.pending} font-ibm`}>
        {status}
      </Badge>
    );
  };

  const getActionBadge = (action) => {
    const variants = {
      login: 'bg-blue-100 text-blue-800 border-blue-200',
      login_attempt: 'bg-purple-100 text-purple-800 border-purple-200',
      create_user: 'bg-green-100 text-green-800 border-green-200',
      update_user: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      delete_user: 'bg-red-100 text-red-800 border-red-200',
      update_settings: 'bg-indigo-100 text-indigo-800 border-indigo-200'
    };

    return (
      <Badge className={`${variants[action] || 'bg-gray-100 text-gray-800 border-gray-200'} font-ibm`}>
        {action.replace(/_/g, ' ')}
      </Badge>
    );
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const clearFilters = () => {
    setFilterAction('all');
    setFilterUser('');
    setStartDate('');
    setEndDate('');
  };

  const exportToCSV = () => {
    if (logs.length === 0) {
      toast.error('No logs to export');
      return;
    }

    // Define CSV headers
    const headers = ['Timestamp', 'Admin', 'Action', 'Target', 'Details', 'Status', 'Region', 'IP Address'];
    
    // Convert logs to CSV format
    const csvRows = [
      headers.join(','),
      ...logs.map(log => [
        `"${formatDate(log.timestamp)}"`,
        `"${log.admin_username || 'N/A'}"`,
        `"${log.action.replace(/_/g, ' ')}"`,
        `"${log.target || 'N/A'}"`,
        `"${(log.details || '').replace(/"/g, '""')}"`,
        `"${log.status || 'N/A'}"`,
        `"${log.region || 'N/A'}"`,
        `"${log.ip_address || 'N/A'}"`
      ].join(','))
    ];

    // Create CSV blob
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Create download link
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `activity_logs_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${logs.length} log entries`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <FileText className="h-8 w-8 text-slate-700" />
              <h1 className="text-3xl font-outfit font-bold text-slate-900">{t('admin.activity_logs.title')}</h1>
            </div>
            <p className="text-slate-600 font-ibm">{t('admin.activity_logs.subtitle')}</p>
          </div>
          <Button
            onClick={exportToCSV}
            className="bg-emerald hover:bg-emerald/90 font-ibm"
            disabled={logs.length === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            Export to CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-slate-600" />
              <CardTitle className="font-outfit text-lg">Filters</CardTitle>
            </div>
            {(filterAction !== 'all' || filterUser || startDate || endDate) && (
              <Button
                onClick={clearFilters}
                variant="outline"
                size="sm"
                className="font-ibm text-xs"
              >
                Clear Filters
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-ibm font-medium text-slate-700">Action Type</label>
              <Select value={filterAction} onValueChange={setFilterAction}>
                <SelectTrigger className="font-ibm">
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All actions</SelectItem>
                  <SelectItem value="login">Login</SelectItem>
                  <SelectItem value="login_attempt">Login Attempts</SelectItem>
                  <SelectItem value="create_user">Create User</SelectItem>
                  <SelectItem value="update_user">Update User</SelectItem>
                  <SelectItem value="delete_user">Delete User</SelectItem>
                  <SelectItem value="update_settings">Update Settings</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-ibm font-medium text-slate-700">Admin Username</label>
              <Input
                type="text"
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                placeholder="Filter by username..."
                className="font-ibm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-ibm font-medium text-slate-700">
                <Clock className="h-3 w-3 inline mr-1" />
                Start Date
              </label>
              <Input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="font-ibm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-ibm font-medium text-slate-700">
                <Clock className="h-3 w-3 inline mr-1" />
                End Date
              </label>
              <Input
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="font-ibm"
              />
            </div>
          </div>

          {(startDate || endDate) && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 font-ibm">
                <strong>Date Range:</strong> {startDate ? new Date(startDate).toLocaleString() : 'Any'} → {endDate ? new Date(endDate).toLocaleString() : 'Now'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Logs List */}
      <Card>
        <CardHeader>
          <CardTitle className="font-outfit">Recent Activity</CardTitle>
          <CardDescription className="font-ibm">
            Showing {logs.length} log entries
          </CardDescription>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-8 text-slate-500 font-ibm">
              <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No activity logs found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {logs.map((log, index) => (
                <div
                  key={index}
                  className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {getActionBadge(log.action)}
                      {getStatusBadge(log.status)}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600 font-ibm">
                      <Clock className="h-4 w-4" />
                      {formatDate(log.timestamp)}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <User className="h-4 w-4 text-slate-500" />
                      <span className="font-ibm font-medium text-slate-700">
                        {log.admin_username}
                      </span>
                      <span className="text-slate-500 font-ibm">on</span>
                      <span className="font-ibm text-slate-600">{log.target}</span>
                    </div>

                    <p className="text-sm text-slate-600 font-ibm pl-6">{log.details}</p>

                    <div className="flex items-start gap-4 pl-6 text-xs text-slate-500 font-ibm">
                      {log.ip_address && (
                        <div className="flex items-center gap-1">
                          <span className="font-medium">IP:</span>
                          <span>{log.ip_address}</span>
                        </div>
                      )}
                      {log.region && log.region !== 'Unknown' && (
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          <span className="font-medium">Region:</span>
                          <span>{log.region}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
